import { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import '../../styles/map.css';
import { useLanguage } from '../../utils/LanguageContext';
import { getLocalized } from '../../utils/languageUtils';
import api from '../../services/api';

// ─── Color palette per tree family ────────────────────────────────────────────
const FAMILY_COLORS = {
  'Mevali daraxtlar': '#2d9e6b',
  'Manzarali daraxtlar': '#5c62d6',
  'Butalar': '#e07b39',
  'Manzarali gullar': '#c94f7c',
};
const DEFAULT_COLOR = '#2d9e6b';

// Backend family names -> translation keys, so labels follow the chosen language.
const FAMILY_LABEL_KEYS = {
  'Mevali daraxtlar': 'filter_fruit',
  'Manzarali daraxtlar': 'filter_decorative',
  'Manzarali gullar': 'map_cat_flowers',
  'Butalar': 'map_cat_bushes',
};

function familyColor(type) {
  return FAMILY_COLORS[type] || DEFAULT_COLOR;
}

// ─── GeoJSON builder ──────────────────────────────────────────────────────────
// A tree without real coordinates used to fall back to (0, 0) — a point in the
// Atlantic Ocean — so it never showed up over Uzbekistan. Only map real locations.
const hasLocation = (t) => Number.isFinite(t.lat) && Number.isFinite(t.lng) && !(t.lat === 0 && t.lng === 0);

function buildGeoJSON(trees) {
  return {
    type: 'FeatureCollection',
    features: trees.filter(hasLocation).map(t => ({
      type: 'Feature',
      id: t.id,
      geometry: { type: 'Point', coordinates: [t.lng, t.lat] },
      properties: {
        id: t.id,
        color: familyColor(t.type),
        type: t.type,
        // serialise complex objects to JSON strings for MapLibre
        commonJson: JSON.stringify(t.common),
        descJson: JSON.stringify(t.treeDesc),
        regionJson: JSON.stringify(t.region),
        owner: t.owner,
        plantedBy: t.plantedBy,
        plantingDate: t.plantingDate,
        plantingYear: t.plantingYear,
        age: t.age,
        co2: t.co2,
        imagesJson: JSON.stringify(t.images || []),
      },
    })),
  };
}

// ─── Main component ───────────────────────────────────────────────────────────
const TreeMap = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const popup = useRef(null);          // tiny hover tooltip
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();

  const [selectedTree, setSelectedTree] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState('all');
  const [showOnlyMyTrees, setShowOnlyMyTrees] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // ── Fetch current user ──────────────────────────────────────────────────────
  useEffect(() => {
    api.get('get/me/').then(r => setCurrentUserId(r.data.id)).catch(() => { });
  }, []);

  // ── Fetch real trees from backend ───────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get('map/');
        const mapped = res.data.map(p => ({
          id: p.id,
          common: {
            en: p.bucket?.product?.tree?.name_en || 'Unknown Tree',
            ru: p.bucket?.product?.tree?.name_ru || 'Derevo',
            uz: p.bucket?.product?.tree?.name_uz || 'Daraxt',
          },
          scientific: p.bucket?.product?.tree?.family?.name || 'N/A',
          type: p.bucket?.product?.tree?.family?.name || 'Unknown',
          // The API spells this field "lognitude"; accept the correct spelling too.
          lat: parseFloat(p.latitude),
          lng: parseFloat(p.longitude ?? p.lognitude),
          owner: p.bucket?.user?.name || 'Anonymous',
          ownerId: p.bucket?.user?.id || null,
          ownerUserId: p.bucket?.user?.user?.id || null,
          plantedBy: p.plantedBy?.name || 'Unknown',
          plantedById: p.plantedBy?.user?.id || null,
          plantedByProfileId: p.plantedBy?.id || null,
          region: {
            en: p.bucket?.user?.region || 'Uzbekistan',
            ru: p.bucket?.user?.region || 'Uzbekistan',
            uz: p.bucket?.user?.region || "O'zbekiston",
          },
          plantingDate: p.plantingDate,
          plantingYear: p.plantingDate ? new Date(p.plantingDate).getFullYear() : 2024,
          age: p.plantingDate ? Math.max(0, new Date().getFullYear() - new Date(p.plantingDate).getFullYear()) : 1,
          images: p.images?.map(i => i.image) || [],
          treeDesc: {
            en: p.bucket?.product?.tree?.desc_en || '',
            ru: p.bucket?.product?.tree?.desc_ru || '',
            uz: p.bucket?.product?.tree?.desc_uz || '',
          },
          co2: Math.round(Math.max(0, new Date().getFullYear() - new Date(p.plantingDate).getFullYear()) * 22),
          healthBenefits: ['map_benefit_air', 'map_benefit_shade', 'map_benefit_mental'],
        }));
        setTrees(mapped);
      } catch (err) {
        console.error('Failed to load map data:', err);
      }
      setLoading(false);
    };
    load();
  }, []);

  // ── Navigate to specific tree from ?tree=ID query param ─────────────────────
  useEffect(() => {
    const treeId = searchParams.get('tree');
    if (!treeId || trees.length === 0 || !map.current) return;

    const targetTree = trees.find(t => t.id === parseInt(treeId));
    if (!targetTree) return;

    // Open sidebar with tree details
    setSelectedTree({
      id: targetTree.id,
      type: targetTree.type,
      common: targetTree.common,
      treeDesc: targetTree.treeDesc,
      region: targetTree.region,
      images: targetTree.images,
      owner: targetTree.owner,
      plantedBy: targetTree.plantedBy,
      plantingDate: targetTree.plantingDate,
      age: targetTree.age,
      co2: targetTree.co2,
      healthBenefits: ['map_benefit_air', 'map_benefit_shade', 'map_benefit_mental'],
    });

    // Fly to the tree location
    if (targetTree.lat && targetTree.lng) {
      map.current.flyTo({
        center: [targetTree.lng, targetTree.lat],
        zoom: 16,
        speed: 0.9,
        curve: 1.3,
        offset: [210, 0],
      });
    }
  }, [trees, searchParams]);

  // ── Filtered view ───────────────────────────────────────────────────────────
  const filteredTrees = trees.filter(tree => {
    if (activeFilter !== 'all' && tree.type !== activeFilter) return false;
    if (yearFilter !== 'all' && tree.plantingYear !== parseInt(yearFilter)) return false;
    // get/me/ may return either the profile id or the auth user id, so match against both.
    if (showOnlyMyTrees && currentUserId &&
      ![tree.ownerId, tree.ownerUserId, tree.plantedById, tree.plantedByProfileId].includes(currentUserId)) return false;
    if (searchQuery.trim() && !getLocalized(tree.common, language).toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const familyLabel = (name) => (FAMILY_LABEL_KEYS[name] ? t(FAMILY_LABEL_KEYS[name]) : name);
  const treesWithoutLocation = filteredTrees.filter(tree => !hasLocation(tree)).length;
  const totalCO2 = filteredTrees.reduce((s, t) => s + (t.co2 || 0), 0);
  const uniqueRegions = [...new Set(filteredTrees.map(t => getLocalized(t.region, language)).filter(Boolean))].length;

  // ── Update GeoJSON source whenever filters change ───────────────────────────
  const updateSource = useCallback(() => {
    if (!map.current || !map.current.isStyleLoaded()) return;
    const src = map.current.getSource('trees');
    if (src) src.setData(buildGeoJSON(filteredTrees));
  }, [filteredTrees]); // eslint-disable-line

  useEffect(() => { updateSource(); }, [updateSource]);

  // ── Init map (once, after loading) ─────────────────────────────────────────
  useEffect(() => {
    if (map.current || !mapContainer.current || loading) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      // CARTO's free raster tiles now return an "API key required" placeholder,
      // so use OpenFreeMap (free, no key, OpenStreetMap data, attribution built in).
      style: 'https://tiles.openfreemap.org/styles/positron',
      center: [68.783, 40.490],
      zoom: 12,
    });

    map.current.addControl(new maplibregl.NavigationControl(), 'bottom-right');

    map.current.on('load', () => {
      // ── Add GeoJSON source ──────────────────────────────────────────────────
      map.current.addSource('trees', {
        type: 'geojson',
        data: buildGeoJSON(filteredTrees),
        cluster: false,
        promoteId: 'id',
      });

      // ── Subtle ambient glow ─────────────────────────────────────────────────
      map.current.addLayer({
        id: 'trees-glow',
        type: 'circle',
        source: 'trees',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'],
            10, 5, 14, 8, 18, 14],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.15,
          'circle-blur': 0.6,
        },
      });

      // ── Main dot (NYC style with feature-state) ────────────────────────────
      map.current.addLayer({
        id: 'trees-layer',
        type: 'circle',
        source: 'trees',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'],
            10, 2.5, 12, 3.5, 14, 5, 17, 8],
          'circle-color': ['get', 'color'],
          'circle-opacity': 0.88,
          'circle-stroke-width': [
            'case',
            ['boolean', ['feature-state', 'selected'], false], 3.5,
            ['boolean', ['feature-state', 'hover'], false], 2.5,
            1.0
          ],
          'circle-stroke-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false], '#fff200',
            ['boolean', ['feature-state', 'hover'], false], '#ffffff',
            '#ffffff'
          ],
          'circle-radius-transition': { duration: 300 },
          'circle-opacity-transition': { duration: 250 },
          'circle-stroke-width-transition': { duration: 150 },
        },
      });

      // ── Feature-state hover tracking ─────────────────────────────────────────
      let hoveredId = null;
      let selectedId = null;

      map.current.on('mousemove', 'trees-layer', (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
        if (e.features.length > 0) {
          if (hoveredId !== null) {
            map.current.setFeatureState({ source: 'trees', id: hoveredId }, { hover: false });
          }
          hoveredId = e.features[0].id;
          map.current.setFeatureState({ source: 'trees', id: hoveredId }, { hover: true });

          // Tooltip
          if (popup.current) { popup.current.remove(); popup.current = null; }
          const props = e.features[0].properties;
          const name = JSON.parse(props.commonJson)[language] || JSON.parse(props.commonJson).en;
          popup.current = new maplibregl.Popup({ closeButton: false, offset: 12, className: 'map-tooltip' })
            .setLngLat(e.features[0].geometry.coordinates)
            .setHTML(`<div class="map-tooltip-inner"><strong>${name}</strong><span>${props.type}</span></div>`)
            .addTo(map.current);
        }
      });

      map.current.on('mouseleave', 'trees-layer', () => {
        map.current.getCanvas().style.cursor = '';
        if (hoveredId !== null) {
          map.current.setFeatureState({ source: 'trees', id: hoveredId }, { hover: false });
          hoveredId = null;
        }
        if (popup.current) { popup.current.remove(); popup.current = null; }
      });

      // ── Click: select dot + smooth flyTo (NYC style) ────────────────────────
      map.current.on('click', 'trees-layer', (e) => {
        if (e.features.length === 0) return;
        const feature = e.features[0];
        const p = feature.properties;

        // Highlight selected dot (gold stroke)
        if (selectedId !== null) {
          map.current.setFeatureState({ source: 'trees', id: selectedId }, { selected: false });
        }
        selectedId = feature.id;
        map.current.setFeatureState({ source: 'trees', id: selectedId }, { selected: true });

        const tree = {
          id: p.id,
          type: p.type,
          common: JSON.parse(p.commonJson),
          treeDesc: JSON.parse(p.descJson),
          region: JSON.parse(p.regionJson),
          images: JSON.parse(p.imagesJson),
          owner: p.owner,
          plantedBy: p.plantedBy,
          plantingDate: p.plantingDate,
          age: p.age,
          co2: p.co2,
          healthBenefits: ['map_benefit_air', 'map_benefit_shade', 'map_benefit_mental'],
        };
        setSelectedTree(tree);

        // Gentle flyTo like NYC (smooth 1.2s, zoom to street level)
        map.current.flyTo({
          center: feature.geometry.coordinates,
          zoom: 17.5,
          duration: 1200,
          essential: true,
          padding: { left: 400 },
        });
      });

      // ── Click on blank: deselect + close sidebar ───────────────────────────
      map.current.on('click', (e) => {
        const features = map.current.queryRenderedFeatures(e.point, { layers: ['trees-layer'] });
        if (!features.length) {
          if (selectedId !== null) {
            map.current.setFeatureState({ source: 'trees', id: selectedId }, { selected: false });
            selectedId = null;
          }
          setSelectedTree(null);
        }
      });

      // Populate now that source exists
      map.current.getSource('trees').setData(buildGeoJSON(filteredTrees));

      // Auto-fit map bounds to show all trees evenly. Only trees with a real
      // location count: with none, keep the default Uzbekistan view instead of
      // zooming into empty ocean.
      const located = filteredTrees.filter(hasLocation);
      if (located.length > 0) {
        const lngs = located.map(t => t.lng);
        const lats = located.map(t => t.lat);
        const bounds = [
          [Math.min(...lngs), Math.min(...lats)],
          [Math.max(...lngs), Math.max(...lats)],
        ];
        map.current.fitBounds(bounds, {
          padding: { top: 150, bottom: 120, left: 470, right: 80 },
          maxZoom: 16,
          duration: 0,
        });
      }
    });

    // eslint-disable-next-line
  }, [loading]);

  const closeSidebar = () => {
    setSelectedTree(null);
    map.current?.flyTo({ zoom: 12, center: [68.783, 40.490] });
  };

  const clearAllFilters = () => { setActiveFilter('all'); setYearFilter('all'); setShowOnlyMyTrees(false); setSearchQuery(''); };

  if (loading) return (
    <div className="map-loading">
      <div className="loading-spinner"></div>
      <p>{t('shop_loading')}</p>
    </div>
  );

  return (
    <div className="map-page">

      {/* Category Filter Bar */}
      <div className="category-filter-bar">
        {[
          { key: 'all', icon: 'fa-tree', label: t('map_cat_all') },
          { key: 'Mevali daraxtlar', icon: 'fa-apple-whole', label: t('map_cat_fruit') },
          { key: 'Manzarali daraxtlar', icon: 'fa-tree-city', label: t('map_cat_decorative') },
          { key: 'Manzarali gullar', icon: 'fa-spa', label: t('map_cat_flowers') },
          { key: 'Butalar', icon: 'fa-leaf', label: t('map_cat_bushes') },
        ].map(btn => (
          <button
            key={btn.key}
            className={`category-filter-btn ${activeFilter === btn.key ? 'active' : ''}`}
            onClick={() => setActiveFilter(btn.key)}
          >
            <i className={`fa-solid ${btn.icon}`}></i><span> {btn.label}</span>
          </button>
        ))}
      </div>

      {/* Advanced Filter Panel */}
      <div className="filter-panel-compact">
        <button className="filter-toggle-btn" onClick={() => setShowFilters(v => !v)}>
          <i className="fa-solid fa-sliders"></i> {t('map_filter_btn')}
        </button>
        {showFilters && (
          <div className="filter-dropdown-content">
            <div className="filter-section">
              <label className="filter-section-label">{t('map_year_label')}</label>
              <select className="filter-select-compact" value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
                <option value="all">{t('map_year_all')}</option>
                {[...new Set(trees.map(t => t.plantingYear))].sort((a, b) => b - a).map(y =>
                  <option key={y} value={y}>{y}</option>
                )}
              </select>
            </div>
            <div className="filter-section">
              <label className="filter-section-label">{t('map_search_label')}</label>
              <input type="text" className="filter-search-input" placeholder={t('map_search_ph')}
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <div className="filter-section">
              <label className="filter-checkbox-compact">
                <input type="checkbox" checked={showOnlyMyTrees} onChange={e => setShowOnlyMyTrees(e.target.checked)} />
                <span>{t('map_my_trees')}</span>
              </label>
            </div>
            <div className="filter-stats-compact">
              <div>{t('map_showing')}: <strong>{filteredTrees.length}</strong> / {trees.length}</div>
              <div>CO₂: <strong>{totalCO2.toLocaleString()} kg</strong></div>
            </div>
            {(activeFilter !== 'all' || yearFilter !== 'all' || showOnlyMyTrees || searchQuery) && (
              <button className="clear-all-btn" onClick={clearAllFilters}>
                <i className="fa-solid fa-times"></i> {t('map_clear')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Left Sidebar */}
      <div id="sidebar">
        {!selectedTree ? (
          <div id="welcome-view">
            <div className="welcome-header">
              <h1>{t('map_welcome_title')}</h1>
              <p>{t('map_welcome_desc')}</p>
            </div>
            <div className="guide-card">
              <div style={{ fontWeight: 700, marginBottom: 12, color: 'var(--primary-dark)' }}>{t('map_welcome_explore')}</div>
              {[
                { icon: 'fa-filter', text: t('map_step_filter') },
                { icon: 'fa-hand-pointer', text: t('map_step_click') },
                { icon: 'fa-images', text: t('map_step_photos') },
              ].map((s, i) => (
                <div key={i} className="guide-step">
                  <div className="step-icon"><i className={`fa-solid ${s.icon}`}></i></div>
                  <div className="step-text">{s.text}</div>
                </div>
              ))}
            </div>
            <div style={{ fontWeight: 700, marginTop: 20, color: 'var(--dark)' }}>{t('map_impact_header')}</div>
            <div className="global-stats-grid">
              <div className="g-stat-box">
                <div className="g-stat-val">{filteredTrees.length.toLocaleString()}</div>
                <div className="g-stat-lbl">{t('map_stat_trees_short')}</div>
              </div>
              <div className="g-stat-box">
                <div className="g-stat-val">{totalCO2.toLocaleString()}</div>
                <div className="g-stat-lbl">{t('map_stat_co2_short')}</div>
              </div>
              <div className="g-stat-box">
                <div className="g-stat-val">{uniqueRegions}</div>
                <div className="g-stat-lbl">{t('map_stat_regions')}</div>
              </div>
              <div className="g-stat-box">
                <div className="g-stat-val">{[...new Set(trees.map(t => t.plantingYear))].length}</div>
                <div className="g-stat-lbl">{t('map_stat_years_active')}</div>
              </div>
            </div>
            {treesWithoutLocation > 0 && (
              <div className="map-location-note">
                <img src="/images/stickers/sapling-pin.webp" alt="" />
                <span><strong>{treesWithoutLocation}</strong> {t('map_trees_no_location')}</span>
              </div>
            )}
            {/* Legend */}
            <div className="map-legend">
              {Object.entries(FAMILY_COLORS).map(([fam, clr]) => (
                <div key={fam} className="legend-item">
                  <span className="legend-dot" style={{ background: clr }}></span>
                  <span className="legend-label">{familyLabel(fam)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div id="detail-view">
            <button className="close-btn" onClick={closeSidebar}><i className="fa-solid fa-times"></i></button>
            <div className="carousel-container">
              <div className="species-overlay">{familyLabel(selectedTree.type)}</div>
              {selectedTree.images?.length > 0 ? (
                <div className="image-track">
                  {selectedTree.images.map((img, i) => <img key={i} src={img} className="carousel-img" alt="Tree" />)}
                </div>
              ) : (
                <div className="placeholder-image">
                  <i className="fa-solid fa-tree" style={{ fontSize: '80px', opacity: 0.15 }}></i>
                </div>
              )}
            </div>
            <div className="content">
              <h1 style={{ fontSize: '1.8rem', color: 'var(--dark)', lineHeight: 1.2 }}>
                {getLocalized(selectedTree.common, language)}
              </h1>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-light)', marginBottom: 20 }}>{familyLabel(selectedTree.type)}</div>
              <div className="impact-section">
                <div className="section-title">🏙️ {t('map_desc_title')}</div>
                <div className="superpower-card">
                  <div className="superpower-text">{getLocalized(selectedTree.treeDesc, language)}</div>
                </div>
              </div>
              <div className="impact-section">
                <div className="section-title">❤️ {t('map_health_title')}</div>
                <div className="health-grid">
                  {selectedTree.healthBenefits.map((k, i) =>
                    <div key={i} className="health-tag"><i className="fa-solid fa-check"></i> {t(k)}</div>
                  )}
                </div>
              </div>
              <div className="meta-grid">
                <div className="meta-box">
                  <div className="meta-label">{t('map_meta_co2')}</div>
                  <div className="meta-val"><i className="fa-solid fa-cloud" style={{ color: 'var(--primary)' }}></i> {selectedTree.co2} kg</div>
                </div>
                <div className="meta-box">
                  <div className="meta-label">{t('map_meta_age')}</div>
                  <div className="meta-val"><i className="fa-solid fa-hourglass-half" style={{ color: 'var(--primary)' }}></i> {selectedTree.age} yr</div>
                </div>
                <div className="meta-box">
                  <div className="meta-label">{t('map_meta_owner')}</div>
                  <div className="meta-val">{selectedTree.owner}</div>
                </div>
                <div className="meta-box">
                  <div className="meta-label">{t('map_meta_planted_by')}</div>
                  <div className="meta-val">{selectedTree.plantedBy}</div>
                </div>
              </div>
              {selectedTree.plantingDate && (
                <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>{t('map_meta_planted_on')}</div>
                  <div style={{ fontWeight: 600, color: 'var(--dark)' }}>
                    {new Date(selectedTree.plantingDate).toLocaleDateString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Stats Overlay */}
      <div className="map-stats-overlay">
        <div className="map-stat-chip">
          <img className="map-stat-chip-icon" src="/images/stickers/sapling-pin.webp" alt="" />
          <span className="map-stat-chip-val">{filteredTrees.length.toLocaleString()}</span>
          <span className="map-stat-chip-lbl">{t('map_stat_trees_short')}</span>
        </div>
        <div className="map-stat-divider" />
        <div className="map-stat-chip">
          <img className="map-stat-chip-icon" src="/images/stickers/leaf.webp" alt="" />
          <span className="map-stat-chip-val">{totalCO2.toLocaleString()}</span>
          <span className="map-stat-chip-lbl">{t('map_stat_co2_short')}</span>
        </div>
        <div className="map-stat-divider" />
        <div className="map-stat-chip">
          <img className="map-stat-chip-icon" src="/images/stickers/map.webp" alt="" />
          <span className="map-stat-chip-val">{uniqueRegions}</span>
          <span className="map-stat-chip-lbl">{t('map_stat_regions')}</span>
        </div>
      </div>

      <div ref={mapContainer} id="map"></div>
    </div>
  );
};

export default TreeMap;